// Wait-mode timing scoring. In wait mode the notes still fall at tempo and the playhead
// pins at the keyline (a note's `onset`) when the player is late — so a real timing error
// is available per gate: held early (continuously, before arrival) → negative; held only
// after the playhead pins → positive (the seconds the note waited). This tallies each gate
// into early / on-time / late by the hit window. Pure leaf — the engine feeds it errors.
import { CONFIG } from "../config";

type TimingClass = "early" | "on-time" | "late";

/** Classify a timing error (seconds; negative = early, positive = late) by the hit window. */
export function classifyTiming(errorSeconds: number, windowSeconds: number): TimingClass {
  if (errorSeconds < -windowSeconds) return "early";
  if (errorSeconds > windowSeconds) return "late";
  return "on-time";
}

export interface WaitTimingStats {
  early: number;
  onTime: number;
  late: number;
  /** Notes played that the current gate didn't expect (accuracy, not timing). */
  wrong: number;
}

/** Accumulates wait-mode gate timing + wrong-note accuracy across a run. */
export class WaitTiming {
  readonly stats: WaitTimingStats = { early: 0, onTime: 0, late: 0, wrong: 0 };
  private readonly window = CONFIG.hitWindowMs / 1000;

  /** Record one gate's timing error (seconds). */
  record(errorSeconds: number): void {
    const cls = classifyTiming(errorSeconds, this.window);
    if (cls === "early") this.stats.early += 1;
    else if (cls === "late") this.stats.late += 1;
    else this.stats.onTime += 1;
  }

  /** Record a wrong-note press (a pitch the current gate didn't expect). */
  recordWrong(): void {
    this.stats.wrong += 1;
  }

  reset(): void {
    this.stats.early = 0;
    this.stats.onTime = 0;
    this.stats.late = 0;
    this.stats.wrong = 0;
  }
}
